import { useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Check, Copy, Link2, Send, Share2, MessageCircle } from "lucide-react";
import { BottomSheet } from "./bottom-sheet";
import { getShareConfig } from "@/lib/share-config";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

function currentUrl(pathname: string) {
  if (typeof window === "undefined") return `https://thequantumminds.in${pathname}`;
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Compact, reusable "Share" control for page headers.
 * Renders nothing when the current page has no share copy defined.
 */
export function ShareButton({ className }: { className?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const config = getShareConfig(pathname);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!config) return null;

  const url = currentUrl(pathname);
  const message = `${config.text} ${url}`;

  const nativeShare = async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: config.title, text: config.text, url });
        setOpen(false);
      }
    } catch {
      /* user dismissed the system sheet */
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    toast.success("Link copied", { duration: 1500 });
    setTimeout(() => setCopied(false), 2000);
  };

  const openExternal = (href: string) => {
    window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Share this page"
        className={cn(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-hairline bg-surface px-3 text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground active:scale-95",
          className,
        )}
      >
        <Share2 className="size-3.5" />
        Share
      </button>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Share"
        subtitle={config.title}
      >
        <div className="space-y-2 pb-2">
          {canNativeShare ? (
            <ShareRow
              icon={<Share2 className="size-4" />}
              label="Share via…"
              hint="Use your device's share sheet"
              onClick={nativeShare}
            />
          ) : null}
          <ShareRow
            icon={<MessageCircle className="size-4" />}
            label="WhatsApp"
            onClick={() =>
              openExternal(`https://wa.me/?text=${encodeURIComponent(message)}`)
            }
          />
          <ShareRow
            icon={<Send className="size-4" />}
            label="Telegram"
            onClick={() =>
              openExternal(
                `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(config.text)}`,
              )
            }
          />
          <ShareRow
            icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            label={copied ? "Link copied" : "Copy link"}
            onClick={copyLink}
          />
        </div>

        <div className="mt-3 flex items-center gap-2 rounded-2xl border border-hairline bg-surface px-3 py-2.5 text-[12px] text-muted-foreground">
          <Link2 className="size-3.5 shrink-0" />
          <span className="truncate">{url}</span>
        </div>
      </BottomSheet>
    </>
  );
}

function ShareRow({
  icon,
  label,
  hint,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl border border-hairline bg-surface px-4 py-3 text-left transition-colors hover:bg-muted/30 active:scale-[0.99]"
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-full border border-hairline text-foreground">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{label}</span>
        {hint ? <span className="block text-[12px] text-muted-foreground">{hint}</span> : null}
      </span>
    </button>
  );
}
